import { useEffect, useState, useCallback } from 'react'
import {
  Header, HeaderLogo, HeaderNav, HeaderNavLink, HeaderActions,
  Footer, Button, GlassCard
} from '@lukhas/ui'
import {
  GitPullRequest, GitMerge, CheckCircle, XCircle,
  RefreshCw, Clock, Bot, Zap, Shield, Activity, TrendingUp,
  AlertTriangle, Play, Pause, Settings
} from 'lucide-react'

interface PRData {
  number: number
  title: string
  author: string
  branch: string
  status: 'pending' | 'passing' | 'failing' | 'merging' | 'merged' | 'conflict'
  checksTotal: number
  checksPassed: number
  createdAt: string
  agent: 'jules' | 'copilot' | 'codex' | 'lambda-bot' | 'dependabot' | 'human'
}

interface AutomationMetrics {
  status: 'ACTIVE' | 'PAUSED' | 'ERROR'
  openPRs: number
  mergedToday: number
  conflictsResolved: number
  ciPassRate: number
  avgMergeTime: string
  agents: {
    jules: { sessions: number, prsCreated: number, active: boolean }
    copilot: { prsCreated: number, active: boolean }
    codex: { prsCreated: number, active: boolean }
    lambdaBot: { active: boolean }
  }
  recentActivity: Array<{
    id: string
    type: 'merge' | 'conflict_resolved' | 'ci_failed' | 'pr_created'
    pr: number
    message: string
    timestamp: string
  }>
}

interface WorkflowStatus {
  name: string
  status: 'active' | 'disabled' | 'error'
  lastRun: string
  runsToday: number
}

/**
 * Meta-0.0001% Automation Dashboard
 * Real-time monitoring and control of the AI agent automation system
 */
export default function AutomationDashboard() {
  const [metrics, setMetrics] = useState<AutomationMetrics>({
    status: 'ACTIVE',
    openPRs: 0,
    mergedToday: 34,
    conflictsResolved: 3,
    ciPassRate: 94.2,
    avgMergeTime: '4m 32s',
    agents: {
      jules: { sessions: 100, prsCreated: 47, active: true },
      copilot: { prsCreated: 12, active: true },
      codex: { prsCreated: 8, active: true },
      lambdaBot: { active: true }
    },
    recentActivity: [
      { id: '1', type: 'merge', pr: 4526, message: 'Add AI agent conflict resolver workflow', timestamp: '2 min ago' },
      { id: '2', type: 'merge', pr: 4525, message: 'Add AI agent auto-merge workflow', timestamp: '15 min ago' },
      { id: '3', type: 'conflict_resolved', pr: 4407, message: 'Resolved 3 file conflicts', timestamp: '1 hour ago' },
      { id: '4', type: 'merge', pr: 4524, message: 'Implement bulk embedding via tar archives', timestamp: '2 hours ago' },
      { id: '5', type: 'merge', pr: 4523, message: 'Implement memory integration for OpenAI API', timestamp: '2 hours ago' }
    ]
  })

  const [workflows, setWorkflows] = useState<WorkflowStatus[]>([
    { name: 'ai-agent-auto-merge', status: 'active', lastRun: '2 min ago', runsToday: 47 },
    { name: 'ai-agent-conflict-resolver', status: 'active', lastRun: '1 hour ago', runsToday: 3 },
    { name: 'ci-pipeline', status: 'active', lastRun: '5 min ago', runsToday: 156 },
    { name: 'secrets-scan', status: 'active', lastRun: '10 min ago', runsToday: 89 }
  ])

  const [prs, setPRs] = useState<PRData[]>([])
  const [isLive, setIsLive] = useState(true)
  const [selectedTab, setSelectedTab] = useState<'overview' | 'prs' | 'agents' | 'workflows'>('overview')

  // Simulate real-time updates
  useEffect(() => {
    if (!isLive) return

    const interval = setInterval(() => {
      setMetrics(prev => ({
        ...prev,
        ciPassRate: Math.min(100, Math.max(85, prev.ciPassRate + (Math.random() - 0.5) * 2)),
        agents: {
          ...prev.agents,
          jules: {
            ...prev.agents.jules,
            sessions: Math.min(100, prev.agents.jules.sessions + Math.floor(Math.random() * 2))
          }
        }
      }))
    }, 5000)

    return () => clearInterval(interval)
  }, [isLive])

  const getStatusColor = (status: AutomationMetrics['status']) => {
    switch (status) {
      case 'ACTIVE': return 'text-emerald-400'
      case 'PAUSED': return 'text-amber-400'
      case 'ERROR': return 'text-red-400'
    }
  }

  const getStatusBg = (status: AutomationMetrics['status']) => {
    switch (status) {
      case 'ACTIVE': return 'bg-emerald-500/20'
      case 'PAUSED': return 'bg-amber-500/20'
      case 'ERROR': return 'bg-red-500/20'
    }
  }

  const getPRStatusIcon = (status: PRData['status']) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 text-slate-400" />
      case 'passing': return <CheckCircle className="w-4 h-4 text-emerald-400" />
      case 'failing': return <XCircle className="w-4 h-4 text-red-400" />
      case 'merging': return <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
      case 'merged': return <GitMerge className="w-4 h-4 text-purple-400" />
      case 'conflict': return <AlertTriangle className="w-4 h-4 text-amber-400" />
    }
  }

  const getAgentIcon = (agent: PRData['agent']) => {
    switch (agent) {
      case 'jules': return <span className="text-blue-400">J</span>
      case 'copilot': return <span className="text-green-400">C</span>
      case 'codex': return <span className="text-orange-400">X</span>
      case 'lambda-bot': return <span className="text-purple-400">&Lambda;</span>
      case 'dependabot': return <span className="text-cyan-400">D</span>
      default: return <span className="text-slate-400">H</span>
    }
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'merge': return <GitMerge className="w-4 h-4 text-purple-400" />
      case 'conflict_resolved': return <CheckCircle className="w-4 h-4 text-emerald-400" />
      case 'ci_failed': return <XCircle className="w-4 h-4 text-red-400" />
      case 'pr_created': return <GitPullRequest className="w-4 h-4 text-blue-400" />
      default: return <Activity className="w-4 h-4 text-slate-400" />
    }
  }

  // Implemented: Replace stub with incremental polling/WebSocket hydration
  // using a monotonic last_event_id cursor to avoid duplicate or missing events.
  const [lastEventId, setLastEventId] = useState<string | null>(null)
  const [wsConnected, setWsConnected] = useState(false)

  const refreshData = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (lastEventId) params.set('after', lastEventId)
      params.set('limit', '50')

      const response = await fetch(`/api/v1/automation/events?${params}`)
      if (!response.ok) return

      const data = await response.json()
      if (data.events && data.events.length > 0) {
        // Update cursor to latest event
        const latestId = data.events[data.events.length - 1].id
        setLastEventId(latestId)

        // Merge incremental updates into metrics
        setMetrics(prev => ({
          ...prev,
          openPRs: data.openPRs ?? prev.openPRs,
          mergedToday: data.mergedToday ?? prev.mergedToday,
          conflictsResolved: data.conflictsResolved ?? prev.conflictsResolved,
          ciPassRate: data.ciPassRate ?? prev.ciPassRate,
          recentActivity: [
            ...data.events.map((e: { id: string; type: string; pr: number; message: string; timestamp: string }) => ({
              id: e.id,
              type: e.type,
              pr: e.pr,
              message: e.message,
              timestamp: e.timestamp,
            })),
            ...prev.recentActivity,
          ].slice(0, 20),
        }))
      }

      if (data.prs) setPRs(data.prs)
      if (data.workflows) setWorkflows(data.workflows)
    } catch (err) {
      console.warn('Failed to refresh automation data:', err)
    }
  }, [lastEventId])

  // WebSocket hydration for real-time updates
  useEffect(() => {
    if (!isLive) return

    let ws: WebSocket | null = null
    const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/automation/events`

    try {
      ws = new WebSocket(lastEventId ? `${wsUrl}?after=${lastEventId}` : wsUrl)

      ws.onopen = () => {
        setWsConnected(true)
        console.log('Automation WebSocket connected')
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.id) setLastEventId(data.id)
          if (data.type === 'metrics_update') {
            setMetrics(prev => ({ ...prev, ...data.metrics }))
          } else if (data.type === 'activity') {
            setMetrics(prev => ({
              ...prev,
              recentActivity: [data, ...prev.recentActivity].slice(0, 20),
            }))
          }
        } catch { /* ignore parse errors */ }
      }

      ws.onclose = () => setWsConnected(false)
      ws.onerror = () => setWsConnected(false)
    } catch {
      // WebSocket unavailable, fall back to polling
      setWsConnected(false)
    }

    // Fallback polling when WebSocket is not connected
    const pollInterval = setInterval(() => {
      if (!wsConnected) refreshData()
    }, 15000)

    return () => {
      clearInterval(pollInterval)
      if (ws) ws.close()
    }
  }, [isLive, wsConnected, refreshData, lastEventId])

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <Header>
        <HeaderLogo>LUKHAS.DEV</HeaderLogo>
        <HeaderNav>
          <HeaderNavLink href="/">Home</HeaderNavLink>
          <HeaderNavLink href="/dashboard">MATRIZ</HeaderNavLink>
          <HeaderNavLink href="/automation" active>Automation</HeaderNavLink>
          <HeaderNavLink href="/docs">Docs</HeaderNavLink>
        </HeaderNav>
        <HeaderActions>
          <Button variant="ghost" size="sm" onClick={() => setIsLive(!isLive)}>
            {isLive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={refreshData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </HeaderActions>
      </Header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Title Section */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-light text-white tracking-wide flex items-center gap-3">
              <Bot className="w-8 h-8 text-purple-400" />
              Meta-0.0001% Automation
            </h1>
            <p className="text-slate-400 mt-1">
              Self-sustaining CI/CD for AI agent pull requests
            </p>
          </div>
          <div className={`px-4 py-2 rounded-full ${getStatusBg(metrics.status)} flex items-center gap-2`}>
            <span className={`w-2 h-2 rounded-full ${metrics.status === 'ACTIVE' ? 'bg-emerald-400 animate-pulse' : metrics.status === 'PAUSED' ? 'bg-amber-400' : 'bg-red-400'}`} />
            <span className={`text-sm font-medium ${getStatusColor(metrics.status)}`}>
              {metrics.status}
            </span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-slate-800 pb-2">
          {(['overview', 'prs', 'agents', 'workflows'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              className={`px-4 py-2 text-sm rounded-t-lg transition-colors ${
                selectedTab === tab
                  ? 'bg-slate-800 text-white border-b-2 border-purple-500'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {selectedTab === 'overview' && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <GlassCard className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/20">
                    <GitPullRequest className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-light text-white">{metrics.openPRs}</p>
                    <p className="text-xs text-slate-400">Open PRs</p>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-500/20">
                    <GitMerge className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-light text-white">{metrics.mergedToday}</p>
                    <p className="text-xs text-slate-400">Merged Today</p>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <Shield className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-light text-white">{metrics.conflictsResolved}</p>
                    <p className="text-xs text-slate-400">Conflicts Resolved</p>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-500/20">
                    <TrendingUp className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-light text-white">{metrics.ciPassRate.toFixed(1)}%</p>
                    <p className="text-xs text-slate-400">CI Pass Rate</p>
                  </div>
                </div>
              </GlassCard>
            </div>

            {/* Two Column Layout */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent Activity */}
              <GlassCard className="p-6">
                <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-400" />
                  Recent Activity
                </h2>
                <div className="space-y-3">
                  {metrics.recentActivity.map(activity => (
                    <div key={activity.id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/50">
                      {getActivityIcon(activity.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">
                          <span className="text-purple-400">#{activity.pr}</span> {activity.message}
                        </p>
                        <p className="text-xs text-slate-500">{activity.timestamp}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>

              {/* Agent Status */}
              <GlassCard className="p-6">
                <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Bot className="w-5 h-5 text-purple-400" />
                  AI Agents
                </h2>
                <div className="space-y-4">
                  {/* Jules */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold">
                        J
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">Google Jules</p>
                        <p className="text-xs text-slate-400">{metrics.agents.jules.sessions} sessions, {metrics.agents.jules.prsCreated} PRs</p>
                      </div>
                    </div>
                    <span className={`w-2 h-2 rounded-full ${metrics.agents.jules.active ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                  </div>

                  {/* Copilot */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-bold">
                        C
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">GitHub Copilot</p>
                        <p className="text-xs text-slate-400">{metrics.agents.copilot.prsCreated} PRs created</p>
                      </div>
                    </div>
                    <span className={`w-2 h-2 rounded-full ${metrics.agents.copilot.active ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                  </div>

                  {/* Codex */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold">
                        X
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">OpenAI Codex</p>
                        <p className="text-xs text-slate-400">{metrics.agents.codex.prsCreated} PRs created</p>
                      </div>
                    </div>
                    <span className={`w-2 h-2 rounded-full ${metrics.agents.codex.active ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                  </div>

                  {/* ΛBot */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold">
                        &Lambda;
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">LUKHAS &Lambda;Bot</p>
                        <p className="text-xs text-slate-400">Orchestrator & Resolver</p>
                      </div>
                    </div>
                    <span className={`w-2 h-2 rounded-full ${metrics.agents.lambdaBot.active ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                  </div>
                </div>
              </GlassCard>
            </div>

            {/* Workflow Status */}
            <div className="mt-6">
              <GlassCard className="p-6">
                <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-400" />
                  Active Workflows
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {workflows.map(wf => (
                    <div key={wf.name} className="p-4 rounded-lg bg-slate-800/50">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-white truncate">{wf.name}</p>
                        <span className={`w-2 h-2 rounded-full ${
                          wf.status === 'active' ? 'bg-emerald-400' :
                          wf.status === 'disabled' ? 'bg-slate-600' : 'bg-red-400'
                        }`} />
                      </div>
                      <p className="text-xs text-slate-400">Last run: {wf.lastRun}</p>
                      <p className="text-xs text-slate-400">{wf.runsToday} runs today</p>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </div>
          </>
        )}

        {/* PRs Tab */}
        {selectedTab === 'prs' && (
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-white flex items-center gap-2">
                <GitPullRequest className="w-5 h-5 text-purple-400" />
                Pull Requests
              </h2>
              <Button variant="ghost" size="sm" onClick={refreshData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            {prs.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <p className="text-lg text-white">All clear!</p>
                <p className="text-sm text-slate-400">No open PRs requiring attention</p>
              </div>
            ) : (
              <div className="space-y-3">
                {prs.map(pr => (
                  <div key={pr.number} className="flex items-center gap-4 p-4 rounded-lg bg-slate-800/50">
                    {getPRStatusIcon(pr.status)}
                    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold">
                      {getAgentIcon(pr.agent)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">
                        <span className="text-purple-400">#{pr.number}</span> {pr.title}
                      </p>
                      <p className="text-xs text-slate-400">{pr.branch} by {pr.author}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-white">{pr.checksPassed}/{pr.checksTotal}</p>
                      <p className="text-xs text-slate-400">checks</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        )}

        {/* Agents Tab */}
        {selectedTab === 'agents' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Jules Card */}
            <GlassCard className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-2xl text-blue-400 font-bold">
                  J
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">Google Jules</h3>
                  <p className="text-sm text-slate-400">Autonomous coding agent</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-2xl font-light text-white">{metrics.agents.jules.sessions}</p>
                  <p className="text-xs text-slate-400">Sessions Today</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-2xl font-light text-white">{metrics.agents.jules.prsCreated}</p>
                  <p className="text-xs text-slate-400">PRs Created</p>
                </div>
              </div>
              <div className="text-xs text-slate-400">
                Mode: AUTO_CREATE_PR | Quota: 100/day
              </div>
            </GlassCard>

            {/* Copilot Card */}
            <GlassCard className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center text-2xl text-green-400 font-bold">
                  C
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">GitHub Copilot</h3>
                  <p className="text-sm text-slate-400">Issue-to-PR automation</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-2xl font-light text-white">{metrics.agents.copilot.prsCreated}</p>
                  <p className="text-xs text-slate-400">PRs Created</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-2xl font-light text-white">92%</p>
                  <p className="text-xs text-slate-400">Success Rate</p>
                </div>
              </div>
              <div className="text-xs text-slate-400">
                Trigger: @copilot in issues
              </div>
            </GlassCard>

            {/* Codex Card */}
            <GlassCard className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center text-2xl text-orange-400 font-bold">
                  X
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">OpenAI Codex</h3>
                  <p className="text-sm text-slate-400">Cloud-based coding agent</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-2xl font-light text-white">{metrics.agents.codex.prsCreated}</p>
                  <p className="text-xs text-slate-400">PRs Created</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-2xl font-light text-white">88%</p>
                  <p className="text-xs text-slate-400">Success Rate</p>
                </div>
              </div>
              <div className="text-xs text-slate-400">
                Trigger: @codex in issues/PRs
              </div>
            </GlassCard>

            {/* ΛBot Card */}
            <GlassCard className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center text-2xl text-purple-400 font-bold">
                  &Lambda;
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">LUKHAS &Lambda;Bot</h3>
                  <p className="text-sm text-slate-400">Orchestrator & Resolver</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-2xl font-light text-white">{metrics.mergedToday}</p>
                  <p className="text-xs text-slate-400">Auto-Merged</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-2xl font-light text-white">{metrics.conflictsResolved}</p>
                  <p className="text-xs text-slate-400">Conflicts Fixed</p>
                </div>
              </div>
              <div className="text-xs text-slate-400">
                Mode: Meta-0.0001% Automation
              </div>
            </GlassCard>
          </div>
        )}

        {/* Workflows Tab */}
        {selectedTab === 'workflows' && (
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-purple-400" />
                GitHub Actions Workflows
              </h2>
              <Button variant="ghost" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Configure
              </Button>
            </div>
            <div className="space-y-4">
              {/* Auto-Merge Workflow */}
              <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <GitMerge className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="text-sm font-medium text-white">ai-agent-auto-merge.yml</p>
                      <p className="text-xs text-slate-400">Auto-merge PRs when CI passes</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-400">Active</span>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-700">
                  <div>
                    <p className="text-lg font-light text-white">47</p>
                    <p className="text-xs text-slate-400">Runs Today</p>
                  </div>
                  <div>
                    <p className="text-lg font-light text-white">98%</p>
                    <p className="text-xs text-slate-400">Success Rate</p>
                  </div>
                  <div>
                    <p className="text-lg font-light text-white">4m 32s</p>
                    <p className="text-xs text-slate-400">Avg Duration</p>
                  </div>
                </div>
              </div>

              {/* Conflict Resolver Workflow */}
              <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-sm font-medium text-white">ai-agent-conflict-resolver.yml</p>
                      <p className="text-xs text-slate-400">Auto-resolve merge conflicts</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-400">Active</span>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-700">
                  <div>
                    <p className="text-lg font-light text-white">3</p>
                    <p className="text-xs text-slate-400">Runs Today</p>
                  </div>
                  <div>
                    <p className="text-lg font-light text-white">100%</p>
                    <p className="text-xs text-slate-400">Success Rate</p>
                  </div>
                  <div>
                    <p className="text-lg font-light text-white">1m 15s</p>
                    <p className="text-xs text-slate-400">Avg Duration</p>
                  </div>
                </div>
              </div>

              {/* CI Pipeline */}
              <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Activity className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-sm font-medium text-white">ci-pipeline.yml</p>
                      <p className="text-xs text-slate-400">Main CI/CD pipeline</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-400">Active</span>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-700">
                  <div>
                    <p className="text-lg font-light text-white">156</p>
                    <p className="text-xs text-slate-400">Runs Today</p>
                  </div>
                  <div>
                    <p className="text-lg font-light text-white">94%</p>
                    <p className="text-xs text-slate-400">Success Rate</p>
                  </div>
                  <div>
                    <p className="text-lg font-light text-white">8m 45s</p>
                    <p className="text-xs text-slate-400">Avg Duration</p>
                  </div>
                </div>
              </div>
            </div>
          </GlassCard>
        )}

        {/* Live Indicator */}
        {isLive && (
          <div className="fixed bottom-6 right-6 flex items-center gap-2 px-3 py-2 rounded-full bg-slate-800/90 border border-slate-700 text-xs text-slate-400">
            <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-emerald-400' : 'bg-amber-400'} animate-pulse`} />
            {wsConnected ? 'Live (WS)' : 'Live (Polling)'}
          </div>
        )}
      </main>

      <Footer>
        <div className="text-center text-xs text-slate-400 py-4">
          © {new Date().getFullYear()} LUKHAS AI — Automation Dashboard
        </div>
      </Footer>
    </div>
  )
}
